import React, { useState, useEffect, useRef, useCallback } from 'react';
import { MessageCircle, Send, Clock, CheckCircle, User, Loader2, X, AlertTriangle, ChevronDown, ChevronUp, Radio, UserCheck, Activity, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';

// Notification sound using Web Audio API
const createNotificationSound = (type = 'message') => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    if (type === 'message') {
      // Friendly poke sound for new message
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(1000, audioContext.currentTime + 0.1);
      oscillator.frequency.setValueAtTime(800, audioContext.currentTime + 0.2);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } else if (type === 'new_chat') {
      // Alert tone for new waiting chat
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(900, audioContext.currentTime + 0.15);
      oscillator.frequency.setValueAtTime(600, audioContext.currentTime + 0.3);
      oscillator.frequency.setValueAtTime(900, audioContext.currentTime + 0.45);
      gainNode.gain.setValueAtTime(0.4, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    } else if (type === 'chat_end') {
      // Lower tone for chat ended
      oscillator.frequency.setValueAtTime(400, audioContext.currentTime);
      oscillator.frequency.setValueAtTime(300, audioContext.currentTime + 0.2);
      oscillator.frequency.setValueAtTime(200, audioContext.currentTime + 0.4);
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    }
  } catch (e) {
    console.log('Audio not supported');
  }
};

const AdminLiveChatPanel = ({ token, API, isCustomerSupport = false, onNewChat = null }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [chats, setChats] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [activeChatMessages, setActiveChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [filter, setFilter] = useState('waiting');
  const [counts, setCounts] = useState({ waiting: 0, active: 0, closed: 0 });
  
  // Agent name prompt state
  const [showAgentNameModal, setShowAgentNameModal] = useState(false);
  const [agentName, setAgentName] = useState('');
  const [pendingChat, setPendingChat] = useState(null);
  
  // Agent status state
  const [isAgentLive, setIsAgentLive] = useState(false);
  const [agentStatus, setAgentStatus] = useState(null);
  
  // Sound state
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const pollChatsRef = useRef(null);
  const pollMessagesRef = useRef(null);
  const prevMessageCountRef = useRef(0);
  const prevWaitingCountRef = useRef(0);
  const prevChatStatusRef = useRef('');
  const isUserScrollingRef = useRef(false);

  // Play notification sound
  const playSound = useCallback((type) => {
    if (soundEnabled) {
      createNotificationSound(type);
    }
  }, [soundEnabled]);

  // Smart auto-scroll - only scroll when new messages arrive and user isn't scrolling up
  useEffect(() => {
    if (activeChatMessages.length > prevMessageCountRef.current && !isUserScrollingRef.current) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      // Play sound for new user messages
      const latestMessage = activeChatMessages[activeChatMessages.length - 1];
      if (latestMessage && latestMessage.sender === 'user') {
        playSound('message');
        // Call external onNewChat callback if provided
        if (onNewChat) {
          onNewChat();
        }
      }
    }
    prevMessageCountRef.current = activeChatMessages.length;
  }, [activeChatMessages, playSound]);

  // Handle scroll to detect if user is reading older messages
  const handleScroll = (e) => {
    const container = e.target;
    const isAtBottom = container.scrollHeight - container.scrollTop <= container.clientHeight + 50;
    isUserScrollingRef.current = !isAtBottom;
  };

  // Fetch agent status on mount
  useEffect(() => {
    fetchAgentStatus();
  }, [token]);

  const fetchAgentStatus = async () => {
    try {
      const res = await fetch(`${API}/admin/agent/status?token=${token}`);
      const data = await res.json();
      if (data.success) {
        setIsAgentLive(data.is_live);
        setAgentStatus(data);
        if (data.agent_name) {
          setAgentName(data.agent_name);
        }
      }
    } catch (err) {
      console.error('Error fetching agent status:', err);
    }
  };

  // Toggle agent live status
  const toggleAgentLive = async () => {
    // If going live and no agent name set, prompt for it
    if (!isAgentLive && !agentName) {
      setShowAgentNameModal(true);
      setPendingChat(null); // Not joining a chat, just going live
      return;
    }
    
    try {
      const res = await fetch(`${API}/admin/agent/toggle-live?token=${token}&is_live=${!isAgentLive}&agent_name=${encodeURIComponent(agentName)}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setIsAgentLive(!isAgentLive);
        toast.success(isAgentLive ? 'You are now offline' : 'You are now live!');
        fetchAgentStatus();
      }
    } catch (err) {
      toast.error('Failed to update status');
    }
  };

  // Fetch chats list
  const fetchChats = async () => {
    try {
      const res = await fetch(`${API}/admin/live-chats?token=${token}&status=${filter === 'all' ? '' : filter}`);
      const data = await res.json();
      if (data.success) {
        const newCounts = data.counts || { waiting: 0, active: 0, closed: 0 };
        
        // Play sound for new waiting chats
        if (newCounts.waiting > prevWaitingCountRef.current && prevWaitingCountRef.current > 0) {
          playSound('new_chat');
          toast.info(`📨 New chat waiting! (${newCounts.waiting} in queue)`, { duration: 5000 });
          // Call external onNewChat callback if provided
          if (onNewChat) {
            onNewChat();
          }
        }
        prevWaitingCountRef.current = newCounts.waiting;
        
        setChats(data.chats || []);
        setCounts(newCounts);
      }
    } catch (err) {
      console.error('Error fetching chats:', err);
    }
  };

  // Fetch active chat messages
  const fetchMessages = async () => {
    if (!activeChat) return;
    try {
      const res = await fetch(`${API}/support/chat/${activeChat.chat_id}`);
      const data = await res.json();
      if (data.success) {
        setActiveChatMessages(data.messages || []);
        
        // Check if chat was closed
        if ((data.status === 'closed' || data.status === 'auto_closed') && prevChatStatusRef.current !== data.status) {
          playSound('chat_end');
          toast.info('💬 This chat has been closed', { duration: 3000 });
        }
        prevChatStatusRef.current = data.status;
        
        // Update chat in list
        setChats(prev => prev.map(c => 
          c.chat_id === activeChat.chat_id 
            ? { ...c, status: data.status, admin_joined: data.admin_joined }
            : c
        ));
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };

  // Poll for updates
  useEffect(() => {
    fetchChats();
    pollChatsRef.current = setInterval(fetchChats, 5000);
    return () => clearInterval(pollChatsRef.current);
  }, [filter, token]);

  useEffect(() => {
    if (activeChat) {
      fetchMessages();
      pollMessagesRef.current = setInterval(fetchMessages, 2000);
    }
    return () => {
      if (pollMessagesRef.current) clearInterval(pollMessagesRef.current);
    };
  }, [activeChat]);

  // Prompt for agent name before joining chat
  const handlePickupChat = (chat) => {
    if (!agentName) {
      setShowAgentNameModal(true);
      setPendingChat(chat);
    } else {
      joinChatWithName(chat, agentName);
    }
  };

  // Submit agent name and join chat
  const handleAgentNameSubmit = () => {
    if (!agentName.trim()) {
      toast.error('Please enter your name');
      return;
    }
    
    setShowAgentNameModal(false);
    
    if (pendingChat) {
      joinChatWithName(pendingChat, agentName);
      setPendingChat(null);
    } else {
      // Just going live, not joining a chat
      toggleAgentLive();
    }
  };

  // Join chat with agent name
  const joinChatWithName = async (chat, name) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/admin/live-chat/${chat.chat_id}/join?token=${token}&agent_name=${encodeURIComponent(name)}`, {
        method: 'POST'
      });
      const data = await res.json();
      if (data.success) {
        setActiveChat({ ...chat, admin_joined: true, admin_name: name });
        toast.success(`Chat picked up! User notified.`);
        fetchChats();
        // Reset scroll tracking
        isUserScrollingRef.current = false;
        prevMessageCountRef.current = 0;
      } else {
        toast.error(data.message || 'Failed to pick up chat');
      }
    } catch (err) {
      toast.error('Failed to pick up chat');
    } finally {
      setLoading(false);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeChat || sendingMessage) return;
    
    setSendingMessage(true);
    try {
      const res = await fetch(
        `${API}/admin/live-chat/${activeChat.chat_id}/message?token=${token}&message=${encodeURIComponent(newMessage)}&agent_name=${encodeURIComponent(agentName)}`,
        { method: 'POST' }
      );
      const data = await res.json();
      if (data.success) {
        setNewMessage('');
        fetchMessages();
        // Scroll to bottom after sending
        isUserScrollingRef.current = false;
      } else {
        toast.error('Failed to send message');
      }
    } catch (err) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  // Close chat
  const handleCloseChat = async (chatId) => {
    try {
      await fetch(`${API}/support/chat/${chatId}/close?reason=admin_closed`, { method: 'POST' });
      toast.success('Chat closed');
      setActiveChat(null);
      fetchChats();
    } catch (err) {
      toast.error('Failed to close chat');
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getWaitTime = (createdAt) => {
    const created = new Date(createdAt);
    const now = new Date();
    const mins = Math.floor((now - created) / 60000);
    return mins < 60 ? `${mins}m` : `${Math.floor(mins / 60)}h ${mins % 60}m`;
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500';
      case 'medium': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-amber-500';
      case 'active': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  return (
    <div className="bg-slate-800/50 border border-white/10 rounded-xl overflow-hidden" data-testid="admin-live-chat-panel">
      {/* Agent Name Modal */}
      {showAgentNameModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
          <div className="bg-slate-900 border border-white/20 rounded-xl p-6 w-96 max-w-[90vw]">
            <h3 className="text-white font-bold text-lg mb-4 flex items-center gap-2">
              <User className="w-5 h-5 text-blue-400" />
              Enter Your Name
            </h3>
            <p className="text-gray-400 text-sm mb-4">
              Your name will be shown to the customer when you pick up the chat.
            </p>
            <Input
              type="text"
              value={agentName}
              onChange={(e) => setAgentName(e.target.value)}
              placeholder="e.g., John from Support"
              className="bg-white/10 border-white/20 text-white mb-4"
              autoFocus
              onKeyDown={(e) => e.key === 'Enter' && handleAgentNameSubmit()}
            />
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => { setShowAgentNameModal(false); setPendingChat(null); }}
                className="flex-1 border-white/20 text-gray-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleAgentNameSubmit}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                Continue
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <MessageCircle className="w-5 h-5 text-blue-400" />
          <h3 className="text-white font-bold">Live Chat Support</h3>
          {counts.waiting > 0 && (
            <Badge className="bg-red-500 text-white animate-pulse">
              {counts.waiting} waiting
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-3">
          {/* Sound Toggle */}
          <div
            className="p-2 rounded-full hover:bg-white/10 transition-colors cursor-pointer"
            onClick={(e) => { e.stopPropagation(); setSoundEnabled(!soundEnabled); }}
            title={soundEnabled ? 'Mute notifications' : 'Enable notifications'}
          >
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-green-400" />
            ) : (
              <VolumeX className="w-4 h-4 text-gray-500" />
            )}
          </div>
          {/* Agent Live Status Toggle */}
          <div 
            className="flex items-center gap-2 px-3 py-1 rounded-full cursor-pointer"
            onClick={(e) => { e.stopPropagation(); toggleAgentLive(); }}
          >
            <div className={`w-3 h-3 rounded-full ${isAgentLive ? 'bg-green-500 animate-pulse' : 'bg-gray-500'}`} />
            <span className={`text-sm ${isAgentLive ? 'text-green-400' : 'text-gray-400'}`}>
              {isAgentLive ? 'Live' : 'Offline'}
            </span>
          </div>
          {isExpanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-white/10">
          {/* Agent Status Bar */}
          {agentStatus && (
            <div className="p-3 bg-slate-900/50 border-b border-white/10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-blue-400" />
                  <span className="text-white text-sm font-medium">{agentName || 'Not set'}</span>
                </div>
                {agentStatus.checked_in_at && (
                  <div className="flex items-center gap-2 text-gray-400 text-xs">
                    <Clock className="w-3 h-3" />
                    Checked in: {new Date(agentStatus.checked_in_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                )}
                {agentStatus.today_duration_seconds > 0 && (
                  <div className="flex items-center gap-2 text-green-400 text-xs">
                    <Activity className="w-3 h-3" />
                    Today: {formatDuration(agentStatus.today_duration_seconds)}
                  </div>
                )}
              </div>
              {!agentName && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setShowAgentNameModal(true); setPendingChat(null); }}
                  className="text-xs border-blue-500/50 text-blue-400"
                >
                  Set Name
                </Button>
              )}
            </div>
          )}

          {/* Stats */}
          <div className="p-4 grid grid-cols-3 gap-4 border-b border-white/10">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-400">{counts.waiting}</div>
              <div className="text-xs text-gray-400">Waiting</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-400">{counts.active}</div>
              <div className="text-xs text-gray-400">Active</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-400">{counts.closed}</div>
              <div className="text-xs text-gray-400">Closed</div>
            </div>
          </div>

          {/* Filter tabs */}
          <div className="p-2 flex gap-2 border-b border-white/10">
            {['waiting', 'active', 'closed', 'all'].map(f => (
              <button
                key={f}
                onClick={() => { setFilter(f); setActiveChat(null); }}
                className={`px-3 py-1 rounded-lg text-sm capitalize transition-colors ${
                  filter === f ? 'bg-blue-600 text-white' : 'bg-white/5 text-gray-400 hover:bg-white/10'
                }`}
              >
                {f}
              </button>
            ))}
          </div>

          <div className="flex" style={{ height: '400px' }}>
            {/* Chat List */}
            <div className="w-1/3 border-r border-white/10 overflow-y-auto">
              {chats.length === 0 ? (
                <div className="p-4 text-center text-gray-500 text-sm">
                  No {filter} chats
                </div>
              ) : (
                chats.map(chat => (
                  <div
                    key={chat.chat_id}
                    onClick={() => chat.admin_joined || chat.status === 'active' ? setActiveChat(chat) : null}
                    className={`p-3 border-b border-white/5 cursor-pointer hover:bg-white/5 transition-colors ${
                      activeChat?.chat_id === chat.chat_id ? 'bg-blue-600/20' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-white font-medium text-sm truncate">{chat.user_name}</span>
                      <Badge className={`${getStatusColor(chat.status)} text-white text-xs`}>
                        {chat.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`w-2 h-2 rounded-full ${getPriorityColor(chat.priority)}`} />
                      <span className="text-gray-400 text-xs truncate">{chat.reason}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{chat.user_email}</span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {getWaitTime(chat.created_at)}
                      </span>
                    </div>
                    {!chat.admin_joined && chat.status === 'waiting' && (
                      <Button
                        size="sm"
                        className="w-full mt-2 bg-green-600 hover:bg-green-700 text-xs"
                        onClick={(e) => { e.stopPropagation(); handlePickupChat(chat); }}
                        disabled={loading}
                      >
                        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Pick Up Chat'}
                      </Button>
                    )}
                    {chat.admin_joined && chat.admin_name && (
                      <div className="mt-2 text-xs text-green-400 flex items-center gap-1">
                        <UserCheck className="w-3 h-3" />
                        Handled by: {chat.admin_name}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>

            {/* Chat Window */}
            <div className="flex-1 flex flex-col">
              {!activeChat ? (
                <div className="flex-1 flex items-center justify-center text-gray-500">
                  <div className="text-center">
                    <MessageCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Select a chat to view</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Chat Header */}
                  <div className="p-3 border-b border-white/10 flex items-center justify-between bg-slate-800">
                    <div>
                      <div className="text-white font-medium">{activeChat.user_name}</div>
                      <div className="text-gray-400 text-xs">{activeChat.user_email}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getPriorityColor(activeChat.priority)}>
                        {activeChat.priority}
                      </Badge>
                      {activeChat.status !== 'closed' && activeChat.status !== 'auto_closed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCloseChat(activeChat.chat_id)}
                          className="text-red-400 border-red-400/50 hover:bg-red-500/20"
                        >
                          Close
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Messages */}
                  <div 
                    ref={messagesContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto p-3 space-y-2"
                  >
                    {activeChatMessages.map((msg, idx) => (
                      <div
                        key={idx}
                        className={`flex ${msg.sender === 'admin' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[80%] rounded-lg px-3 py-2 ${
                            msg.sender === 'admin'
                              ? 'bg-blue-600 text-white'
                              : msg.sender === 'system'
                              ? 'bg-gray-700 text-gray-300 text-sm italic'
                              : 'bg-slate-700 text-white'
                          }`}
                        >
                          {msg.sender === 'user' && (
                            <div className="text-xs text-green-300 mb-1">{msg.sender_name}</div>
                          )}
                          {msg.sender === 'admin' && (
                            <div className="text-xs text-blue-200 mb-1">{msg.sender_name}</div>
                          )}
                          <p className="text-sm">{msg.message}</p>
                          <div className="text-xs opacity-60 mt-1 text-right">
                            {formatTime(msg.timestamp)}
                          </div>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Input */}
                  {activeChat.status !== 'closed' && activeChat.status !== 'auto_closed' && (
                    <form onSubmit={handleSendMessage} className="p-3 border-t border-white/10 flex gap-2">
                      <Input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder="Type your response..."
                        className="flex-1 bg-white/10 border-white/20 text-white"
                        disabled={sendingMessage}
                      />
                      <Button
                        type="submit"
                        size="icon"
                        disabled={!newMessage.trim() || sendingMessage}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {sendingMessage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                      </Button>
                    </form>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminLiveChatPanel;
